// ---------------------------------------------------------------------------
// carousel — Swiper fade carousel for the landing hero (and any future
// `[data-carousel]`). Ports the intent of the legacy Swiper + `.video-item`
// rotator in jqueryEvents.js / SwiperCarousel.coffee:
//   * autoplay with a slow cross-fade between slides,
//   * pause every slide's <video> on transition start and play only the
//     active slide's video on transition end (background video handling is
//     folded in here — see videoBg note in the stage report).
// Single-slide carousels skip autoplay/loop so Swiper doesn't warn.
// ---------------------------------------------------------------------------

import Swiper from 'swiper';
import { Autoplay, EffectFade } from 'swiper/modules';

// The legacy stylesheet shows a hero slide with a bare `active` class
// (`.video-item { display: none }` / `.video-item.active { display: block }`),
// which predates Swiper and its own `swiper-slide-active`. Keep the two in step
// so the styling follows the carousel.
const markActiveSlide = (swiper: Swiper): void => {
	swiper.slides.forEach((slide, index) => {
		slide.classList.toggle('active', index === swiper.activeIndex);
	});
};

const playActiveVideo = (swiper: Swiper): void => {
	for (const video of swiper.el.querySelectorAll('video')) video.pause();
	const active = swiper.slides[swiper.activeIndex];
	const video = active?.querySelector<HTMLVideoElement>('video');
	const playback = video?.play();
	if (playback && typeof playback.catch === 'function') {
		playback.catch(() => {
			/* autoplay blocked */
		});
	}
};

export const initCarousel = (): void => {
	const containers = document.querySelectorAll<HTMLElement>('[data-carousel]');
	for (const container of containers) {
		const multiple = container.querySelectorAll('.swiper-slide').length > 1;

		new Swiper(container, {
			modules: [Autoplay, EffectFade],
			effect: 'fade',
			fadeEffect: { crossFade: true },
			slidesPerView: 1,
			loop: multiple,
			allowTouchMove: multiple,
			autoplay: multiple ? { delay: 6000, disableOnInteraction: false } : false,
			on: {
				init: (swiper) => {
					markActiveSlide(swiper);
					playActiveVideo(swiper);
				},
				slideChangeTransitionStart: (swiper) => {
					for (const video of swiper.el.querySelectorAll('video')) video.pause();
				},
				slideChangeTransitionEnd: (swiper) => {
					markActiveSlide(swiper);
					playActiveVideo(swiper);
				},
			},
		});
	}
};
